// filepath: apps/web/src/i18n/useTranslation.ts
//
// FR-UI-CORE-8 — translation bridge over react-i18next.
// FR-I18N-3 — extended locale set to include `uk` and `be`.
//
// Public contract (preserved from FR-UI-CORE-6 / FR-UI-CORE-8 F-3):
//   - `useTranslation()` returns `{ t, locale }`.
//   - `t(key, fallback?)` resolves a key for the active locale. When
//     the key is missing from every resource bundle, i18next returns
//     the key string itself; the FR-UI-CORE-6 / FR-UI-CORE-8 contract
//     additionally says "if a fallback was supplied, prefer the
//     fallback". This module wires that contract on top of
//     react-i18next's `t`.
//   - `Locale = "ru" | "kk" | "en" | "uk" | "be"` — the five v1 shipped
//     locales per ARCHITECTURE_WEB.md §8.
//
// `resolveLocaleLabel(map, locale)` is colocated here so chrome
// strings (i18next) and tenant content (definition locale maps) share
// a single fallback policy (`ru → kk → en → uk → be → ""`). Tenant
// content is the FR-I18N-1 contract shape
// `{ "ru": "...", "kk": "...", "en": "...", "uk": "...", "be": "..." }`
// — independent of react-i18next so renderers don't pull the i18n
// runtime just to look up a tenant field label.
//
// SECURITY:
//   - All strings are static platform chrome (no tenant input).
//   - `t()` never throws; missing-key behavior is well-defined.
//   - `resolveLocaleLabel` returns `""` for an empty map — renderers
//     can detect "label unavailable" without nullish checks.

import { useTranslation as useI18nextTranslation } from "react-i18next";
import {
  DEFAULT_LOCALE,
  FALLBACK_ORDER,
  Locale,
  coerceToLocale,
} from "./locales";

/** Re-export `Locale` for callers that import the alias from this module. */
export type { Locale } from "./locales";

/** Tenant-content locale map (FR-I18N-1 contract shape). */
export type LocaleMap = Partial<Record<Locale, string>>;

/**
 * i18next interpolation shape — the union of value types react-i18next
 * accepts inside a `{{ placeholder }}` substitution. Mirrors
 * react-i18next's TFunction interpolation parameter type so the SPA
 * `t()` bridge does not have to re-narrow per consumer.
 */
export type TranslationInterpolation = Record<
  string,
  string | number | boolean
>;

export interface UseTranslationResult {
  /**
   * Resolve `key` for the active locale.
   *
   * Three call shapes — a union of the pre-existing legacy SPA
   * contract AND react-i18next's native TFunction overloads:
   *   - t(key)                                          — bare key
   *   - t(key, interpolation)                           — i18next-native 2-arg form
   *   - t(key, fallback)                                — legacy SPA 2-arg form
   *   - t(key, fallback, interpolation)                 — legacy SPA 3-arg form
   *
   * Returns `fallback` if a `fallback` argument is supplied AND the
   * key is missing from every resource bundle. Returns the key
   * string itself if no `fallback` is supplied and the key is
   * missing. (i18next's native behavior returns the key string when
   * missing; we override that only when `fallback` is non-undefined.)
   *
   * The optional `interpolation` argument is an i18next interpolation
   * map — passed through to react-i18next's `t()` so resource strings
   * containing `{{placeholder}}` slots can be parameterised without
   * losing the bridge contract (i18next's interpolation engine does
   * the substitution before the value is returned to React).
   * The wrapper here does NOT inspect the interpolation map — it
   * forwards the call unchanged.
   *
   * Overload selection (TS resolves in declaration order):
   *   1. i18next-native: (key, interpolation) — interpolation is the Record
   *                                          map; no fallback.
   *   2. legacy SPA:    (key, fallback?, interpolation?) — fallback is
   *                                          optional string; interpolation
   *                                          is optional Record.
   *
   * This combination satisfies both the legacy SPA callers (which pass
   * a string fallback) AND the i18next-native callers (which pass an
   * interpolation map as the second arg). The bug from the prior
   * iteration was that the i18next-native overload required the
   * fallback parameter (no ?), so it could not be assigned to a
   * consumer parameter typed `(key, fallback?: string) => string`.
   * Widening legacy-overload fallback to optional closes the gap.
   */
  t: {
    (key: string, interpolation: TranslationInterpolation): string;
    (
      key: string,
      fallback?: string,
      interpolation?: TranslationInterpolation,
    ): string;
  };
  /** Currently-active locale code. */
  locale: Locale;
}

/**
 * `useTranslation` — thin reactive wrapper over react-i18next.
 *
 * The active locale is whatever i18next's `i18n.language` currently
 * reports — populated by the bootstrap layer (FR-UI-CORE-2 session
 * store seeded by `tenant-config.default_locale`) or by the
 * `?locale=` debug override (no user-facing locale switcher yet;
 * F-5 deferral).
 */
export function useTranslation(ns?: string | string[]): UseTranslationResult {
  const { t: rawT, i18n } = useI18nextTranslation(ns);

  const locale = coerceToLocale(i18n.language);

  // Overloaded implementation matching the public t() contract in
  // UseTranslationResult above. We branch on whether the second arg
  // is an interpolation object (Record) or a fallback string:
  //   - string second arg -> it IS the fallback
  //   - non-string second arg (interpolation map or undefined) -> no fallback
  // We narrow with typeof to keep the bridge contract intact.
  function t(
    key: string,
    secondArg?: string | TranslationInterpolation,
    interpolation?: TranslationInterpolation,
  ): string {
    let fallback: string | undefined;
    let interp: TranslationInterpolation | undefined;
    if (typeof secondArg === "string") {
      fallback = secondArg;
      interp = interpolation;
    } else if (secondArg !== undefined) {
      interp = secondArg;
    } else {
      interp = interpolation;
    }
    const resolved = interp !== undefined ? rawT(key, interp) : rawT(key);
    // i18next returns the key unchanged when the key is absent from
    // every resource bundle. The FR-UI-CORE-6 / FR-UI-CORE-8 contract
    // says: when a `fallback` argument is supplied, prefer it.
    if (resolved === key && fallback !== undefined) {
      return fallback;
    }
    // i18next's rawT return can be typed as `string | undefined` in some
    // overloads; the bridge declares `: string` so coalesce undefined to
    // the key (which i18next guarantees to be returned when missing).
    return resolved ?? key;
  }

  return { t, locale };
}

/**
 * Resolve a tenant-content locale map for the active locale, walking
 * the `ru → kk → en → uk → be` fallback chain. Returns `""` when no
 * entry is found at any level — renderers can detect "label unavailable"
 * via falsy check.
 *
 * Independent of react-i18next: this helper does NOT touch the
 * i18next runtime and never throws. The same fallback policy is
 * shared with `useTranslation().t()` so chrome and tenant content
 * degrade identically.
 *
 * @param map     Definition-payload locale map (FR-I18N-1 contract).
 * @param locale  Currently-active locale.
 * @param fallback Optional final fallback. Defaults to `""`.
 */
export function resolveLocaleLabel(
  map: LocaleMap | undefined | null,
  locale: Locale,
  fallback = "",
): string {
  if (!map) return fallback;
  const direct = map[locale];
  if (typeof direct === "string" && direct.length > 0) return direct;
  for (const fb of FALLBACK_ORDER) {
    const value = map[fb];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return fallback;
}

/** Re-export `DEFAULT_LOCALE` for callers that need it. */
export { DEFAULT_LOCALE };
